/**
 * Data freshness indicator cell for a single source.
 *
 * Display format: [●Xs] with color-coded indicator dot.
 *
 * PERFORMANCE: Timer is managed by parent component (VehicleTable) to avoid
 * creating 1600+ individual timers. Parent passes currentTime as prop.
 */

import { memo } from 'react';

import {
  getFreshnessLevel,
  getFreshnessColorClass,
  getAgeSeconds,
} from '../../utils/freshness';

export interface DataFreshnessCellProps {
  /** Timestamp of last update, or null if never received */
  lastUpdateTime: number | null;
  /** Current time from parent (shared timer) */
  currentTime: number;
  /** Label for accessibility (e.g., "GPS", "Estimated") */
  label?: string;
}

export const DataFreshnessCell = memo(function DataFreshnessCell({
  lastUpdateTime,
  currentTime,
  label = 'Data',
}: DataFreshnessCellProps) {
  // Calculate freshness using parent-provided currentTime
  const level = getFreshnessLevel(lastUpdateTime, currentTime);
  const rawAgeSeconds = getAgeSeconds(lastUpdateTime, currentTime);
  // Ensure non-negative (can happen briefly if timestamp updated after currentTime was cached)
  const ageSeconds = rawAgeSeconds !== null ? Math.max(0, rawAgeSeconds) : null;
  const colorClass = getFreshnessColorClass(level);
  const displayAge = ageSeconds !== null ? `${ageSeconds}s` : '-';

  // Build accessible description
  const status =
    ageSeconds !== null ? `${label} ${ageSeconds} seconds ago` : `${label} no data`;

  return (
    <span
      className="inline-flex items-center gap-1"
      title={status}
      aria-label={status}
    >
      <span className={`w-2 h-2 rounded-full ${colorClass}`} aria-hidden="true" />
      <span className="text-xs text-gray-600 dark:text-gray-300 font-mono">{displayAge}</span>
    </span>
  );
});
